import { Button, Card, Heading, Text } from "@/design-system";
import { Linkedin } from "lucide-react";

/**
 * Perfil de autor do blog.
 *
 * **Isto não existe no domínio.** `BlogPost` só carrega `authorName` (string)
 * e `authorId`; não há `BlogAuthor`, nem endpoint, nem hook para buscá-lo. Em
 * vez de inventar um serviço, o tipo mora aqui e o card recebe tudo por prop:
 * no dia em que o backend entregar o autor, a página passa a preencher os
 * campos e o componente não muda.
 *
 * Todos os campos além do nome são opcionais de propósito — hoje só o nome
 * chega, e o card precisa ficar honesto (e não quebrado) sem os outros.
 */
export interface AuthorProfile {
  /** Nome que assina os posts. É o único dado que existe hoje. */
  name: string;
  /** Cargo/função ("Especialista em crediário"). Precisa de `BlogAuthor` no backend. */
  role?: string | null;
  /** Bio curta, 1 a 3 frases. Precisa de `BlogAuthor` no backend. */
  bio?: string | null;
  /** URL da foto. Precisa de `BlogAuthor` no backend. */
  photoUrl?: string | null;
  /** Perfil no LinkedIn — o sinal de autoridade mais barato de verificar. */
  linkedinUrl?: string | null;
}

/** Iniciais para o lugar da foto: no máximo duas, primeira e última palavra. */
const iniciais = (nome: string): string => {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  const primeira = partes[0][0] ?? "";
  const ultima = partes.length > 1 ? (partes[partes.length - 1][0] ?? "") : "";
  return (primeira + ultima).toUpperCase();
};

/**
 * Cabeçalho da página de autor: foto (ou iniciais), nome, cargo, bio e
 * LinkedIn.
 *
 * Contrastes: iniciais `ds-on-solid` sobre `ds-brand-600` = 4,91:1; nome
 * `ds-ink-900` sobre `ds-surface` = 17,23:1; cargo e bio `ds-ink-700` = 9,49:1.
 *
 * Em 360px o bloco é uma coluna, com a foto acima do nome; a partir de 640px a
 * foto vai para o lado, porque aí a linha aguenta os dois.
 */
export const AuthorProfileCard = ({ author }: { author: AuthorProfile }) => (
  <Card className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
    {author.photoUrl ? (
      <img
        src={author.photoUrl}
        alt={`Foto de ${author.name}`}
        width={96}
        height={96}
        className="size-24 shrink-0 rounded-full object-cover"
      />
    ) : (
      // Sem foto, as iniciais. Decorativo: o nome está escrito ao lado, então
      // repeti-lo para quem escuta a página seria eco.
      <div
        aria-hidden="true"
        className="bg-ds-brand-600 text-ds-on-solid font-ds-display text-ds-h2 flex size-24 shrink-0 items-center justify-center rounded-full font-bold"
      >
        {iniciais(author.name)}
      </div>
    )}

    <div className="flex min-w-0 flex-col gap-2">
      <Heading level={1}>{author.name}</Heading>

      {author.role && (
        <Text size="lead" tone="muted">
          {author.role}
        </Text>
      )}

      {author.bio && (
        <Text tone="muted" className="max-w-prose">
          {author.bio}
        </Text>
      )}

      {author.linkedinUrl && (
        <Button
          asChild
          variant="secondary"
          size="sm"
          navigates="external"
          iconLeft={<Linkedin aria-hidden="true" />}
          className="mt-2 w-auto"
        >
          <a href={author.linkedinUrl} target="_blank" rel="noopener noreferrer me">
            Ver perfil no LinkedIn
          </a>
        </Button>
      )}
    </div>
  </Card>
);
